/**
 * Slice 5 — Reports: jobs list + job evidence (Before / After / Keys / Maintenance).
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ChevronRight, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useCleaningSubmissions } from "@/hooks/useCleaningSubmissions";
import { usePropertyWithAccess } from "@/hooks/useProperties";
import { useMaintenanceReports } from "@/hooks/useMaintenanceReports";
import type { JobEvidenceTask } from "@/components/owner/JobEvidenceView";
import { JobReportView } from "@/components/owner/JobReportView";
import { Pill } from "@/components/owner/JobReportParts";
import { lowFromSubmission, reportCounts, submissionForTask, type MarkLike, type SubmissionRow } from "@/lib/jobReport";
import { collectSimplePhotoUrls, readSimpleFlowState } from "@/lib/simpleTurnover";
import { formatPersonLabel } from "@/lib/taskAssigneeLabel";
import { useAuth } from "@/contexts/AuthContext";
import { startOfWeek, endOfWeek } from "date-fns";
import { useUnitItems } from "@/hooks/useUnitItems";

interface ReportsTabProps {
  propertyId: string;
  onGoToUnit?: () => void;
  onGoToInventory?: () => void;
}

type CleaningTaskRow = JobEvidenceTask & {
  id: string;
  rating?: number | null;
  rating_note?: string | null;
  rated_at?: string | null;
  scheduled_date?: string | null;
  cleaner_id?: string | null;
  completed_at?: string | null;
  checklist_completed?: boolean | null;
  inventory_completed?: boolean | null;
  entry_walkthrough_state?: unknown;
};

type ReportRow = {
  id: string;
  submitted_at: string;
  cleaning_task_id: string | null;
  completion_percentage: number | null;
  cleaners: { name?: string } | null;
  cleaning_tasks: CleaningTaskRow | null;
};

function keysChip(task: CleaningTaskRow | null): { label: string; className: string } {
  const simple = readSimpleFlowState(task?.entry_walkthrough_state);
  const drop =
    simple.keys_drop_photo_url ||
    (simple.keys_photo_url &&
    task?.closeout_keys_photo_url &&
    task.closeout_keys_photo_url !== simple.keys_photo_url
      ? task.closeout_keys_photo_url
      : !simple.keys_photo_url
        ? task?.closeout_keys_photo_url
        : null);
  if (drop) {
    return { label: "Keys left", className: "bg-[#e2eeed] text-[#15776d]" };
  }
  return { label: "No dropoff", className: "bg-[#fdf3e5] text-[#b45309]" };
}

function displayCleanerName(row: ReportRow, ownerName?: string | null): string {
  if ((row.cleaning_tasks as { is_test?: boolean } | null)?.is_test && !row.cleaners?.name) {
    return ownerName ? `${ownerName} (owner)` : "Owner";
  }
  const raw = row.cleaners?.name?.trim();
  if (!raw) return "Cleaner";
  return formatPersonLabel(raw) || raw;
}

const ReportsTab = ({ propertyId, onGoToUnit: _onGoToUnit, onGoToInventory }: ReportsTabProps) => {
  const { data: submissions, isLoading: subsLoading } = useCleaningSubmissions(propertyId);
  const { data: property } = usePropertyWithAccess(propertyId);
  const { data: maintenanceReports = [], isLoading: issuesLoading } = useMaintenanceReports(propertyId);
  const { data: unitItems = [] } = useUnitItems(propertyId);
  const { data: completedTasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: ["reports-completed-tasks", propertyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cleaning_tasks")
        .select(
          "id, checklist_completed, inventory_completed, rating, rating_note, rated_at, closeout_keys_confirmed, closeout_keys_photo_url, closeout_notes, entry_walkthrough_state, duration_minutes, completed_at, scheduled_date, cleaner_id, status, is_test, test_setup, photos_pending, arrival_tasks, started_at, start_location_lat, start_location_lng, start_location_accuracy_m, start_location_distance_m, start_location_verified, start_location_override_reason, start_location_captured_at, finish_location_lat, finish_location_lng, finish_location_accuracy_m, finish_location_distance_m, finish_location_verified, finish_location_override_reason, finish_location_captured_at",
        )
        .eq("property_id", propertyId)
        .or("status.eq.completed,checklist_completed.eq.true")
        .order("completed_at", { ascending: false, nullsFirst: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as unknown as CleaningTaskRow[];
    },
    enabled: !!propertyId,
    staleTime: 60_000,
  });

  const taskIdsForMarks = useMemo(
    () => completedTasks.map((t) => t.id).filter(Boolean),
    [completedTasks],
  );

  const { data: marksByTaskId = {} } = useQuery({
    queryKey: ["reports-unit-check-marks", propertyId, taskIdsForMarks.join(",")],
    queryFn: async () => {
      if (!taskIdsForMarks.length) return {} as Record<string, MarkLike[]>;
      const { data, error } = await supabase
        .from("unit_check_marks")
        .select("cleaning_task_id, unit_item_id, status, issue_report_id")
        .in("cleaning_task_id", taskIdsForMarks);
      if (error) throw error;
      const byTask: Record<string, MarkLike[]> = {};
      for (const row of data ?? []) {
        const tid = row.cleaning_task_id;
        if (!tid) continue;
        (byTask[tid] ??= []).push({
          unit_item_id: row.unit_item_id,
          status: row.status as MarkLike["status"],
          issue_report_id: row.issue_report_id,
        });
      }
      return byTask;
    },
    enabled: taskIdsForMarks.length > 0 && unitItems.length > 0,
    staleTime: 60_000,
  });

  // Inventory the Inv step reported low or empty, matched to each clean by time.
  const { data: invItems = [] } = useQuery({
    queryKey: ["report-inventory-items", propertyId],
    queryFn: async () => {
      const { data } = await supabase
        .from("inventory_items")
        .select("id, item_name, quantity_type, threshold_count, threshold_value")
        .eq("property_id", propertyId);
      return (data ?? []) as { id: string; item_name: string; quantity_type: string; threshold_count: number | null; threshold_value: number | null }[];
    },
    staleTime: 60_000,
  });
  const { data: invSubmissions = [] } = useQuery({
    queryKey: ["report-inventory-submissions", propertyId],
    queryFn: async () => {
      const { data } = await supabase
        .from("inventory_submissions")
        .select("cleaner_id, submitted_at, items")
        .eq("property_id", propertyId)
        .order("submitted_at", { ascending: false })
        .limit(60);
      return (data ?? []) as SubmissionRow[];
    },
    staleTime: 60_000,
  });

  const cleanerIdsFromTasks = useMemo(
    () => [...new Set(completedTasks.map((t) => t.cleaner_id).filter(Boolean))] as string[],
    [completedTasks],
  );

  const { data: cleanerNameById = {} } = useQuery({
    queryKey: ["reports-cleaner-names", cleanerIdsFromTasks.join(",")],
    queryFn: async () => {
      if (!cleanerIdsFromTasks.length) return {} as Record<string, string>;
      const { data, error } = await supabase
        .from("cleaners")
        .select("id, name")
        .in("id", cleanerIdsFromTasks);
      if (error) throw error;
      const map: Record<string, string> = {};
      for (const c of data || []) {
        if (c.id && c.name) map[c.id] = c.name;
      }
      return map;
    },
    enabled: cleanerIdsFromTasks.length > 0,
    staleTime: 60_000,
  });

  const reportsByTaskId = useMemo(() => {
    const map = new Map<string, typeof maintenanceReports>();
    for (const r of maintenanceReports) {
      const tid = r.cleaning_task_id;
      if (!tid) continue;
      const list = map.get(tid) ?? [];
      list.push(r);
      map.set(tid, list);
    }
    return map;
  }, [maintenanceReports]);

  const [openId, setOpenId] = useState<string | null>(null);
  const { profile } = useAuth();
  const ownerName = (profile as { full_name?: string | null } | null)?.full_name?.trim() || null;
  const [showTests, setShowTests] = useState(true);

  const allRows: ReportRow[] = useMemo(() => {
    const fromSubs: ReportRow[] = (submissions ?? []).map((s) => {
      const task = (s.cleaning_tasks as CleaningTaskRow | null) ?? null;
      const fromSub = (s.cleaners as { name?: string } | null) ?? null;
      const submissionCleanerId = (s as { cleaner_id?: string | null }).cleaner_id ?? null;
      const taskWithCleaner: CleaningTaskRow | null = task
        ? {
            ...task,
            cleaner_id: task.cleaner_id ?? submissionCleanerId,
          }
        : null;
      const taskCleanerId = taskWithCleaner?.cleaner_id ?? null;
      const fallbackName =
        !fromSub?.name && taskCleanerId ? cleanerNameById[taskCleanerId] : undefined;
      return {
        id: s.id as string,
        submitted_at: s.submitted_at as string,
        cleaning_task_id: (s.cleaning_task_id as string | null) ?? null,
        completion_percentage: (s.completion_percentage as number | null) ?? null,
        cleaners: fromSub?.name
          ? fromSub
          : fallbackName
            ? { name: fallbackName }
            : fromSub,
        cleaning_tasks: taskWithCleaner,
      };
    });
    const taskIdsWithSub = new Set(
      fromSubs.map((r) => r.cleaning_task_id).filter(Boolean) as string[],
    );
    const fromTasks: ReportRow[] = completedTasks
      .filter((t) => !taskIdsWithSub.has(t.id))
      .map((t) => {
        const name = t.cleaner_id ? cleanerNameById[t.cleaner_id] : undefined;
        return {
          id: `task:${t.id}`,
          submitted_at: t.completed_at || t.scheduled_date || new Date().toISOString(),
          cleaning_task_id: t.id,
          completion_percentage: t.checklist_completed && t.inventory_completed ? 100 : 50,
          cleaners: name ? { name } : null,
          cleaning_tasks: t,
        };
      });
    return [...fromSubs, ...fromTasks].sort(
      (a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime(),
    );
  }, [submissions, completedTasks, cleanerNameById]);

  const isTestRow = (r: ReportRow) => !!(r.cleaning_tasks as { is_test?: boolean } | null)?.is_test;
  const rows = useMemo(() => (showTests ? allRows : allRows.filter((r) => !isTestRow(r))), [allRows, showTests]);
  const testCount = allRows.filter(isTestRow).length;
  const realThisWeek = useMemo(() => {
    const now = new Date();
    const start = startOfWeek(now, { weekStartsOn: 1 }).getTime();
    const end = endOfWeek(now, { weekStartsOn: 1 }).getTime();
    return allRows.filter((r) => {
      if (isTestRow(r)) return false;
      const t = new Date(r.submitted_at).getTime();
      return t >= start && t <= end;
    }).length;
  }, [allRows]);

  const propertyLabel = (property as { name?: string } | null)?.name?.trim() || "Property";
  const openRow = openId ? rows.find((r) => r.id === openId) ?? null : null;
  const isLoading = subsLoading || tasksLoading || issuesLoading;

  if (openRow) {
    const cleaningTask = openRow.cleaning_tasks;
    const cleanerName = displayCleanerName(openRow, ownerName);
    return (
      <JobReportView
        propertyId={propertyId}
        propertyLabel={propertyLabel}
        submittedAt={openRow.submitted_at}
        cleanerName={cleanerName}
        cleaningTask={cleaningTask}
        cleaningTaskId={openRow.cleaning_task_id ?? cleaningTask?.id ?? null}
        onBack={() => setOpenId(null)}
        onGoToInventory={onGoToInventory}
        onTestDeleted={() => setOpenId(null)}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16 text-[#64748b]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-col gap-5" data-tour="tour-reports-empty">
        <h2 className="sr-only">Reports</h2>
        <div className="flex flex-col items-center gap-3 rounded-[18px] border-2 border-dashed border-[#e6edf1] bg-[#f4f7f9] px-6 py-16">
          <div className="flex h-[58px] w-[58px] items-center justify-center rounded-2xl bg-[#e6edf1]">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#8494a2" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="5" width="16" height="15" rx="3" />
              <path d="M8 10h8M8 15h5" />
            </svg>
          </div>
          <div className="text-[22px] font-bold text-[#0f1720]">No jobs</div>
        </div>
      </div>
    );
  }

  const gridCols = "grid-cols-[1.35fr_1.1fr_0.7fr_1.6fr_0.7fr_32px]";

  const rowModel = (row: ReportRow) => {
    const task = row.cleaning_tasks;
    const taskId = task?.id;
    const sub = task ? submissionForTask(invSubmissions, task) : null;
    const lowCount = sub ? lowFromSubmission(sub.items, invItems).length : 0;
    const counts = reportCounts({
      itemIds: unitItems.map((i) => i.id),
      marks: (taskId && Array.isArray(marksByTaskId[taskId]) ? marksByTaskId[taskId] : []) || [],
      flags: (taskId && reportsByTaskId.get(taskId)) || [],
      entryWalkthroughState: task?.entry_walkthrough_state,
      lowCount,
    });
    const simple = readSimpleFlowState(task?.entry_walkthrough_state);
    const guidedShots = Object.values(simple.walk_by_room ?? {}).reduce((n, r) => n + Object.keys(r.shots ?? {}).length, 0);
    const photoCount =
      collectSimplePhotoUrls(simple, "before").length + collectSimplePhotoUrls(simple, "after").length;
    const mins =
      task?.started_at && task?.completed_at
        ? Math.round((new Date(task.completed_at).getTime() - new Date(task.started_at).getTime()) / 60000)
        : null;
    const dur = mins != null && mins >= 0 ? `${Math.floor(mins / 60) ? `${Math.floor(mins / 60)} h ` : ""}${mins % 60} min` : null;
    const sub1 = guidedShots > 0 ? `${guidedShots} guided before shots` : photoCount > 0 ? `${photoCount} photos` : "";
    return { counts, sub: [sub1, dur].filter(Boolean).join(" · ") };
  };

  const pills = (c: ReturnType<typeof rowModel>["counts"]) => {
    const out: { label: string; kind: "missing" | "broken" | "grey" | "here" }[] = [];
    if (c.missing) out.push({ label: `${c.missing} missing`, kind: "missing" });
    if (c.broken) out.push({ label: `${c.broken} broken`, kind: "broken" });
    if (c.flags) out.push({ label: `${c.flags} flag${c.flags === 1 ? "" : "s"}`, kind: "grey" });
    if (!c.maintenance.complete) out.push({ label: "Maintenance not done", kind: "broken" });
    if (c.low) out.push({ label: `${c.low} low on inventory`, kind: "grey" });
    if (out.length === 0) out.push({ label: "All clear", kind: "here" });
    return out;
  };

  return (
    <div className="flex min-w-0 flex-col gap-5" data-tour="tour-reports-list">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="sr-only">Reports</h2>
          <p className="mt-1 text-[14px] font-medium text-[#475569]">
            {realThisWeek} real clean{realThisWeek === 1 ? "" : "s"} this week. Rows show what’s wrong, not how much was checked. Test cleans never count in stats or billing.
          </p>
        </div>
        {testCount > 0 ? (
          <button
            type="button"
            onClick={() => setShowTests((v) => !v)}
            className="flex h-12 items-center gap-3 rounded-[14px] bg-white pl-4 pr-2"
          >
            <span className="flex flex-col items-start">
              <span className="text-[15px] font-bold text-[#0f1720]">Show test cleans</span>
              <span className="text-[12px] font-medium text-[#64748b]">
                {showTests ? `${testCount} in the list` : `${testCount} hidden`}
              </span>
            </span>
            <span className={cn("flex h-[30px] w-[50px] rounded-full p-[3px]", showTests ? "justify-end bg-[#24a89a]" : "justify-start bg-[#d7e0e6]")}>
              <span className="h-6 w-6 rounded-full bg-white shadow-sm" />
            </span>
          </button>
        ) : null}
      </div>

      <div className="hidden overflow-hidden rounded-[24px] bg-white px-2 md:block">
        <div className={cn("grid gap-3 border-b-[1.5px] border-[#eef2f5] px-5 py-4 text-[12px] font-bold tracking-[0.6px] text-[#8494a2]", gridCols)}>
          <div>CLEAN</div>
          <div>CLEANER</div>
          <div>KEYS</div>
          <div>EXCEPTIONS</div>
          <div>STATUS</div>
          <div />
        </div>
        {rows.map((row) => {
          const keys = keysChip(row.cleaning_tasks);
          const { counts, sub } = rowModel(row);
          const cleanerName = displayCleanerName(row, ownerName);
          const isTest = isTestRow(row);
          return (
            <button
              key={row.id}
              type="button"
              onClick={() => setOpenId(row.id)}
              className={cn(
                "grid w-full items-center gap-3 border-b-[1.5px] border-[#f1f5f8] px-5 py-4 text-left last:border-b-0 hover:bg-[#f8fafb]",
                gridCols,
              )}
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="text-[16px] font-bold tracking-[-0.2px] text-[#0f1720]">
                    {format(new Date(row.submitted_at), "EEE d MMM, yyyy")}
                  </div>
                  {isTest ? (
                    <span className="inline-flex h-6 shrink-0 items-center rounded-full bg-[#0f1720] px-2.5 text-[12px] font-bold text-white">
                      Test clean
                    </span>
                  ) : null}
                  {(row.cleaning_tasks as { photos_pending?: number | null } | null)?.photos_pending ? (
                    <span className="inline-flex h-6 shrink-0 items-center rounded-full bg-[#eef2f5] px-2.5 text-[12px] font-bold text-[#64748b]">
                      Waiting to send from the cleaner’s phone
                    </span>
                  ) : null}
                </div>
                {sub ? <div className="text-[12.5px] font-medium text-[#94a3b8]">{sub}</div> : null}
              </div>
              <div className="flex min-w-0 items-center gap-2.5">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#e2eeed] text-[12.5px] font-bold text-[#15776d]">
                  {cleanerName[0]?.toUpperCase() ?? "?"}
                </span>
                <span className="truncate text-[15px] font-bold text-[#0f1720]">{cleanerName}</span>
              </div>
              <div>
                <span className={cn("inline-flex rounded-full px-2.5 py-1 text-xs font-bold", keys.className)}>{keys.label}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {pills(counts).map((p) => (
                  <Pill key={p.label} kind={p.kind} small>
                    {p.label}
                  </Pill>
                ))}
              </div>
              <div>
                <Pill kind={counts.clear ? "here" : "grey"} small>
                  {counts.clear ? "Done" : "Review"}
                </Pill>
              </div>
              <div className="flex justify-end text-[#b9c4cc]">
                <ChevronRight className="h-5 w-5" />
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-3 md:hidden">
        {rows.map((row) => {
          const keys = keysChip(row.cleaning_tasks);
          const { counts, sub } = rowModel(row);
          const cleanerName = displayCleanerName(row, ownerName);
          const isTest = isTestRow(row);
          return (
            <button
              key={row.id}
              type="button"
              onClick={() => setOpenId(row.id)}
              className={cn("flex flex-col gap-2.5 rounded-[20px] bg-white p-4 text-left", isTest && "border-[1.5px] border-dashed border-[#0f1720]")}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="text-[16px] font-bold text-[#0f1720]">{format(new Date(row.submitted_at), "EEE d MMM")}</div>
                    {isTest ? (
                      <span className="inline-flex h-6 shrink-0 items-center rounded-full bg-[#0f1720] px-2.5 text-[12px] font-bold text-white">
                        Test clean
                      </span>
                    ) : null}
                  </div>
                  {(row.cleaning_tasks as { photos_pending?: number | null } | null)?.photos_pending ? (
                    <div className="mt-1 inline-flex rounded-full bg-[#eef2f5] px-2.5 py-1 text-[11.5px] font-bold text-[#64748b]">
                      Waiting to send from the cleaner’s phone
                    </div>
                  ) : null}
                  <div className="mt-0.5 text-[13.5px] font-medium text-[#64748b]">
                    {cleanerName}
                    {sub ? ` · ${sub}` : ""}
                  </div>
                </div>
                <Pill kind={counts.clear ? "here" : "grey"} small>
                  {counts.clear ? "Done" : "Review"}
                </Pill>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className={cn("inline-flex rounded-full px-2.5 py-1 text-[11.5px] font-bold", keys.className)}>{keys.label}</span>
                {pills(counts).map((p) => (
                  <Pill key={p.label} kind={p.kind} small>
                    {p.label}
                  </Pill>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ReportsTab;
